/**
 * Spanish Language Profile
 *
 * SVO word order, prepositions, space-separated.
 * Features rich verb conjugation with pro-drop (subject omission).
 */

import type { LanguageProfile } from './types';

export const spanishProfile: LanguageProfile = {
  code: 'es',
  name: 'Spanish',
  nativeName: 'Español',
  regions: ['western', 'priority'],
  direction: 'ltr',
  script: 'latin',
  wordOrder: 'SVO',
  markingStrategy: 'preposition',
  usesSpaces: true,
  // Infinitive is standard for Spanish software UI (Guardar, Cancelar, Abrir)
  // This matches macOS, Windows, and web app conventions
  defaultVerbForm: 'infinitive',
  verb: {
    position: 'start',
    subjectDrop: true,
  },
  references: {
    me: 'yo', // "I/me" (mí, mi are alternatives handled in possessive)
    it: 'ello', // "it"
    you: 'tú', // "you"
    result: 'resultado',
    event: 'evento',
    target: 'objetivo', // destino is a synonym
    body: 'cuerpo',
    document: 'documento',
    window: 'ventana',
    detail: 'detalle',
  },
  possessive: {
    marker: 'de', // Spanish uses "de" for general possession
    markerPosition: 'before-property',
    usePossessiveAdjectives: true,
    specialForms: {
      me: 'mi', // "my" (possessive adjective)
      it: 'su', // "its"
      you: 'tu', // "your"
    },
    keywords: {
      mi: 'me', // Also accepts mí (with accent)
      tu: 'you',
      su: 'it',
    },
  },
  roleMarkers: {
    // `hacia` is the i18n grammar's optional destination render form ("towards");
    // without it here a rendered/user `hacia` clause silently dropped the
    // destination (add → default `me`, put → null parse). Vocab Batch 1 (V2+V4).
    destination: { primary: 'en', alternatives: ['sobre', 'a', 'hacia'], position: 'before' },
    source: { primary: 'de', alternatives: ['desde'], position: 'before' },
    patient: { primary: '', position: 'before' },
    style: { primary: 'con', position: 'before' },
  },
  // Imperative command forms are accepted on INPUT only — `primary` stays the
  // dictionary form, so rendering is unchanged (see LanguageProfile.defaultVerbForm:
  // infinitive is the industry standard for UI localization). Hyperscript is a
  // command language, though, and a native speaker giving a command writes the
  // imperative, so the parser should read it.
  //
  // Only the IRREGULARS are listed. The regular ones reach their keyword through
  // the morphological normalizer's stem (see spanish-keyword.ts and siblings),
  // which also covers conjugations nobody enumerated here.
  keywords: {
    // Class/Attribute operations
    toggle: { primary: 'alternar', alternatives: ['conmutar', 'toggle'], normalized: 'toggle' },
    add: { primary: 'agregar', alternatives: ['añadir'], normalized: 'add' },
    remove: {
      primary: 'quitar',
      alternatives: ['eliminar', 'remover', 'sacar', 'borrar'],
      normalized: 'remove',
    },
    // Content operations
    put: { primary: 'poner', alternatives: ['colocar', 'pon'], normalized: 'put' },
    append: { primary: 'anexar', normalized: 'append' },
    prepend: { primary: 'anteponer', normalized: 'prepend' },
    take: { primary: 'tomar', normalized: 'take' },
    make: { primary: 'hacer', alternatives: ['crear'], normalized: 'make' },
    clone: { primary: 'clonar', alternatives: ['duplicar'], normalized: 'clone' },
    swap: { primary: 'intercambiar', alternatives: ['permutar'], normalized: 'swap' },
    morph: { primary: 'transformar', alternatives: ['convertir'], normalized: 'morph' },
    // Variable operations
    set: {
      primary: 'establecer',
      alternatives: ['fijar', 'definir', 'establece'],
      normalized: 'set',
    },
    get: { primary: 'obtener', alternatives: ['conseguir', 'obtén'], normalized: 'get' },
    increment: { primary: 'incrementar', alternatives: ['aumentar'], normalized: 'increment' },
    decrement: { primary: 'decrementar', alternatives: ['disminuir'], normalized: 'decrement' },
    log: { primary: 'registrar', alternatives: ['imprimir'], normalized: 'log' },
    // Visibility
    show: { primary: 'mostrar', alternatives: ['enseñar', 'muestra'], normalized: 'show' },
    hide: { primary: 'ocultar', alternatives: ['esconder'], normalized: 'hide' },
    transition: { primary: 'transición', alternatives: ['animar'], normalized: 'transition' },
    // Events
    on: { primary: 'en', alternatives: ['al'], normalized: 'on' },
    trigger: { primary: 'disparar', alternatives: ['activar'], normalized: 'trigger' },
    send: { primary: 'enviar', alternatives: ['envía'], normalized: 'send' },
    // DOM focus
    focus: { primary: 'enfocar', alternatives: ['enfoque'], normalized: 'focus' },
    blur: { primary: 'desenfocar', alternatives: ['desenfoque'], normalized: 'blur' },
    // Phase 1 (v0.9.90): DOM / form state / debug
    open: { primary: 'abrir', normalized: 'open' },
    close: { primary: 'cerrar', normalized: 'close' },
    select: { primary: 'seleccionar', normalized: 'select' },
    clear: { primary: 'limpiar', normalized: 'clear' },
    reset: { primary: 'reiniciar', alternatives: ['restablecer'], normalized: 'reset' },
    breakpoint: {
      primary: 'punto-interrupción',
      alternatives: ['punto-interrupcion'],
      normalized: 'breakpoint',
    },
    // Common event names (for event handler patterns)
    click: { primary: 'clic', alternatives: ['hacer clic', 'click'], normalized: 'click' },
    // `resize` event (window-resize): dict emits redimensionar; register it so the
    // event types as literal="resize" (matching en) instead of expression.
    resize: { primary: 'redimensionar', normalized: 'resize' },
    hover: { primary: 'sobrevolar', alternatives: ['pasar por encima'], normalized: 'hover' },
    submit: { primary: 'envío', alternatives: ['envio', 'someter'], normalized: 'submit' },
    input: { primary: 'entrada', alternatives: ['introducir'], normalized: 'input' },
    change: { primary: 'cambio', alternatives: ['cambiar'], normalized: 'change' },
    // i18n dict emits the verb `cargar` for `load` (profile primary is the noun
    // `carga`); recognize it so `on load` events type as a literal, not expression.
    load: { primary: 'carga', alternatives: ['cargar'], normalized: 'load' },
    scroll: { primary: 'desplazar', alternatives: ['desplazamiento'], normalized: 'scroll' },
    keydown: { primary: 'tecla abajo', normalized: 'keydown' },
    keyup: { primary: 'tecla arriba', normalized: 'keyup' },
    mouseover: { primary: 'ratón encima', alternatives: ['raton encima'], normalized: 'mouseover' },
    mouseout: { primary: 'ratón fuera', alternatives: ['raton fuera'], normalized: 'mouseout' },
    // mousedown/mouseup (repeat-until-event): dict emits ratónabajo/ratónarriba —
    // register so both the on.event and the repeat until-event type as literal.
    mousedown: { primary: 'ratónabajo', normalized: 'mousedown' },
    mouseup: { primary: 'ratónarriba', normalized: 'mouseup' },
    // Navigation
    go: { primary: 'ir', alternatives: ['navegar', 've'], normalized: 'go' },
    push: { primary: 'empujar', alternatives: ['push'], normalized: 'push' },
    replace: { primary: 'reemplazar', alternatives: ['sustituir'], normalized: 'replace' },
    process: { primary: 'procesar', normalized: 'process' },
    // Async
    wait: { primary: 'esperar', normalized: 'wait' },
    fetch: { primary: 'buscar', alternatives: ['recuperar'], normalized: 'fetch' },
    settle: { primary: 'estabilizar', normalized: 'settle' },
    // Control flow
    if: { primary: 'si', normalized: 'if' },
    unless: { primary: 'menos', alternatives: ['a menos que', 'salvo'], normalized: 'unless' },
    when: { primary: 'cuando', normalized: 'when' },
    where: { primary: 'donde', normalized: 'where' },
    else: { primary: 'sino', alternatives: ['de lo contrario'], normalized: 'else' },
    repeat: { primary: 'repetir', normalized: 'repeat' },
    for: { primary: 'para', normalized: 'for' },
    while: { primary: 'mientras', normalized: 'while' },
    // `repeat forever` loop keyword. The i18n dict never translated `forever`, so the
    // corpus leaves it English (`repetir forever`) — the bare word then typed
    // `loopType:expression` (SVO) / `loopType:reference` (SOV) instead of EN's
    // `:literal` (the repeat.loopType R1 residue). Recognizing the English form the
    // corpus carries lets the generated repeat pattern type it as a literal, matching
    // EN. (Native translation belongs to a separate i18n-dict pass; until then the
    // corpus word IS the English one, so that is the primary here.)
    forever: { primary: 'forever', normalized: 'forever' },
    continue: { primary: 'continuar', normalized: 'continue' },
    halt: { primary: 'detener', alternatives: ['parar'], normalized: 'halt' },
    throw: { primary: 'lanzar', alternatives: ['arrojar'], normalized: 'throw' },
    call: { primary: 'llamar', normalized: 'call' },
    return: { primary: 'retornar', alternatives: ['devolver'], normalized: 'return' },
    then: { primary: 'entonces', alternatives: ['luego'], normalized: 'then' },
    and: { primary: 'y', alternatives: ['además', 'también'], normalized: 'and' },
    or: { primary: 'o', normalized: 'or' },
    not: { primary: 'no', normalized: 'not' },
    is: { primary: 'es', normalized: 'is' },
    exists: { primary: 'existe', normalized: 'exists' },
    empty: { primary: 'vacío', alternatives: ['vacio'], normalized: 'empty' },
    // Comparison operator (`target matches .x`). Without this keyword the surface
    // stays an identifier and leaks verbatim into the condition's raw expression,
    // which the core expression parser reads as English (modal-close-backdrop /
    // focus-trap drop their then-branch). Not an ActionType and has no command
    // schema, so no pattern is generated from it.
    matches: { primary: 'coincide', normalized: 'matches' },
    // Negative-existence operator (`if no dragHandle set dragHandle to me`). Same
    // seam as `exists`: without the keyword the surface stays an identifier and
    // leaks verbatim into the condition's raw expression (behavior-draggable).
    // Neither an ActionType nor a command schema, so no pattern is generated from it.
    // Does NOT collide with `not: { primary: 'no' }`: the keyword map is keyed by
    // SURFACE, so this registers `ningún` and leaves the `no` surface untouched.
    no: { primary: 'ningún', normalized: 'no' },
    end: { primary: 'fin', alternatives: ['final', 'terminar'], normalized: 'end' },
    // Advanced
    js: { primary: 'js', normalized: 'js' },
    async: { primary: 'asíncrono', alternatives: ['asincrono'], normalized: 'async' },
    tell: { primary: 'decir', normalized: 'tell' },
    default: { primary: 'predeterminar', alternatives: ['por defecto'], normalized: 'default' },
    init: { primary: 'iniciar', alternatives: ['inicializar'], normalized: 'init' },
    behavior: { primary: 'comportamiento', normalized: 'behavior' },
    install: { primary: 'instalar', normalized: 'install' },
    measure: { primary: 'medir', normalized: 'measure' },
    beep: { primary: 'pitido', normalized: 'beep' },
    break: { primary: 'romper', normalized: 'break' },
    copy: { primary: 'copiar', normalized: 'copy' },
    exit: { primary: 'salir', normalized: 'exit' },
    pick: { primary: 'escoger', normalized: 'pick' },
    render: { primary: 'renderizar', normalized: 'render' },
    // Positional expressions
    first: { primary: 'primero', alternatives: ['primera'], normalized: 'first' },
    last: { primary: 'último', alternatives: ['ultima'], normalized: 'last' },
    next: { primary: 'siguiente', normalized: 'next' },
    previous: { primary: 'anterior', normalized: 'previous' },
    closest: { primary: 'cercano', normalized: 'closest' },
    parent: { primary: 'padre', normalized: 'parent' },
    // Modifiers
    into: { primary: 'dentro', alternatives: ['adentro', 'dentro de'], normalized: 'into' },
    before: { primary: 'antes', alternatives: ['antes de'], normalized: 'before' },
    after: {
      primary: 'después',
      alternatives: ['despues', 'después de', 'despues de'],
      normalized: 'after',
    },
    out: { primary: 'fuera', alternatives: ['fuera de'], normalized: 'out' },
    // Event modifiers (for repeat until event)
    until: { primary: 'hasta', alternatives: ['hasta que'], normalized: 'until' },
    event: { primary: 'evento', normalized: 'event' },
    from: { primary: 'de', alternatives: ['desde'], normalized: 'from' },
    // Phase 8 (htmx v4 localized attributes): attribute-suffix keywords
    // used by the vocab generator — `sse-conectar`, `hx-en-vivo`, etc.
    connect: { primary: 'conectar', alternatives: ['conectarse'], normalized: 'connect' },
    stream: { primary: 'transmitir', alternatives: ['flujo'], normalized: 'stream' },
    live: { primary: 'en-vivo', alternatives: ['vivo', 'directo'], normalized: 'live' },
    socket: { primary: 'socket', alternatives: ['websocket'], normalized: 'socket' },
    // Reactive / realtime commands
    bind: { primary: 'vincular', alternatives: ['enlazar', 'bind'], normalized: 'bind' },
    intercept: { primary: 'interceptar', alternatives: ['intercept'], normalized: 'intercept' },
    worker: { primary: 'trabajador', alternatives: ['worker'], normalized: 'worker' },
    eventsource: {
      primary: 'eventsource',
      alternatives: ['fuente-de-eventos'],
      normalized: 'eventsource',
    },
  },
  eventHandler: {
    keyword: { primary: 'al', alternatives: ['cuando', 'en'], normalized: 'on' },
    sourceMarker: { primary: 'de', alternatives: ['desde'], position: 'before' },
    // Event marker: al (when), used in SVO pattern
    // Pattern: al [event] [verb] [patient] en [destination?]
    // Example: al clic alternar .active en #button
    // + the eventHandler.keyword word the i18n transformer actually emits —
    // without it every generated fused `<cmd>-event-*-vso` pattern was dead
    // (the swap/if recovery split, #346/#351)
    eventMarker: { primary: 'al', alternatives: ['cuando', 'en'], position: 'before' },
    temporalMarkers: ['cuando', 'al'], // temporal conjunctions (when)
  },
};
